import { computed, DestroyRef, Signal, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { EMPTY, merge, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';

export interface CreateResourceOptions<TTrigger, TData> {
  trigger: Signal<TTrigger>;
  guard: (v: TTrigger) => v is NonNullable<TTrigger>;
  fetch: (v: NonNullable<TTrigger>) => Observable<TData>;
  empty: TData;
  distinct?: boolean;
  destroyRef: DestroyRef;
}

export type ResourceSignal<TData> = Signal<TData> & { reload: () => void };

export function createResource<TTrigger, TData>({
  trigger,
  guard,
  fetch,
  empty,
  distinct = false,
  destroyRef,
}: CreateResourceOptions<TTrigger, TData>): ResourceSignal<TData> {
  const data = signal<TData>(empty);
  const reloadTrigger = signal(0);

  const trigger$ = toObservable(trigger).pipe(distinct ? distinctUntilChanged() : (s$) => s$);

  const reload$ = toObservable(reloadTrigger).pipe(map(() => trigger()));

  merge(trigger$, reload$)
    .pipe(
      switchMap((value) => {
        if (!guard(value)) {
          data.set(empty);
          return EMPTY;
        }
        return fetch(value);
      }),
      takeUntilDestroyed(destroyRef),
    )
    .subscribe((result) => data.set(result));

  const result = computed(() => data()) as ResourceSignal<TData>;
  result.reload = () => reloadTrigger.update((version) => version + 1);
  return result;
}
